/**
 * One "which kind of account?" row, shared by the two surfaces that ask that question: the activation
 * wizard's `AccountTypeStep` and this drawer's own step 1.
 *
 * It exists because those two rendered the same markup twice, and the copies drifted the moment one
 * was touched: the wizard gained brand marks, balanced icon padding and single-line rows while the
 * drawer kept a generic key icon, a lopsided 12px/4px gutter and a `line-clamp-2` description that
 * made the Google Ads row stand taller than the rest. Same question, same list, two different
 * answers — which is exactly what a user notices when they meet both in one session.
 *
 * What is NOT shared is which definitions each surface offers, or what it does with the chosen one:
 *
 *  - the WIZARD filters to activation destinations (`resolvableAccountDefinitions`) and derives a
 *    destination from the definition (`accountTypeChoiceOf`, which throws on an unknown one);
 *  - this DRAWER lists whatever `listDefinitions` returns — it is the Settings account surface, and
 *    may legitimately offer a definition that no activation destination uses.
 *
 * Sharing the row and not the list is the whole point: pushing the wizard's filter into the drawer
 * would silently hide account types from Settings, which is a worse bug than the duplication was.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';

/** One definition as `listDefinitions` returns it — derived from the router, never re-declared. */
type Definition = Awaited<
  ReturnType<RouterClient<AppRouter>['settings']['rudderstackAccounts']['listDefinitions']>
>['definitions'][number];

export interface AccountTypeRowProps {
  definition: Definition;
  onSelect: () => void;
}

export function AccountTypeRow({ definition, onSelect }: AccountTypeRowProps): React.JSX.Element {
  // The BRAND mark, resolved from the definition's own `destinationType`. `destinationTypeIcon`
  // falls back to a generic mark for a destination we ship no artwork for, rather than inventing a
  // brand — which is what lets the unfiltered drawer use this row safely.
  const { Icon } = destinationTypeIcon(definition.destinationType);

  return (
    <Button
      type="button"
      variant="secondary"
      size="lg"
      // `gap-3` matches `px-3`, so the space before the mark and the space after it are equal. The
      // Button base is `gap-1`; without this override the mark sits 12px from the card edge and 4px
      // from the text.
      className="h-auto w-full justify-start gap-3 px-3 py-3 text-left whitespace-normal"
      onClick={onSelect}
      Icon={<Icon className="size-5 shrink-0" />}
      suffixIcon={
        definition.authenticationType === 'oauth' ? (
          <Badge variant="outline" className="ml-auto shrink-0 text-xs">
            OAuth
          </Badge>
        ) : undefined
      }
      label={
        // Two lines, each pinned to ONE line, so every row in the column is the same height. The
        // definition's `description` is deliberately absent: most are short, but Google Ads' runs to
        // a sentence and a half, and it was the least useful thing on the row — the destination names
        // itself above, the auth method below, and an OAuth definition already carries a badge.
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate font-medium text-foreground">{definition.destinationDisplayName}</span>
          <span className="truncate text-xs text-muted-foreground">{definition.displayName}</span>
        </span>
      }
    />
  );
}
